import { useQuery } from "react-query";
import { getAllTasks } from "../functions/api_fethcers";
import { useAuthContext } from "../context/AuthContext"; // Import auth context

/**
 * Hook to fetch all tasks across all projects using the simplified all-tasks endpoint
 * @param {Object} options - Query options
 * @returns {Object} Tasks data and query state
 */
export const useAllTasks = (options = {}) => {
  const { isAuthenticated } = useAuthContext(); // Get auth status
  const {
    filters = {},
    enabled = true, // Original enabled option
    staleTime = 60 * 1000, // Keep data fresh for a minute instead of refetching constantly
    refetchOnMount = true, // Always refetch when component mounts
  } = options;

  // Fetch all tasks with a single API call
  const {
    data: tasks = [],
    isLoading,
    error,
    refetch, // Get refetch function directly
  } = useQuery(["all-tasks", filters], () => getAllTasks(filters), {
    enabled: isAuthenticated() && enabled, // Only enable if authenticated AND original enabled is true
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus: false, // Avoid refetching the full task list on every window focus
  });

  // Apply client-side filtering if needed
  let filteredTasks = tasks;

  // Filter by search term if specified
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filteredTasks = filteredTasks.filter(
      (task) =>
        task.name.toLowerCase().includes(searchLower) ||
        (task.description &&
          task.description.toLowerCase().includes(searchLower))
    );
  }

  // Extract unique projects from tasks for grouping
  const projects = [...new Set(tasks.map((task) => task.project_id))]
    .filter(Boolean)
    .map((projectId) => {
      const task = tasks.find((t) => t.project_id === projectId);
      return {
        id: projectId,
        name: task?.project_name || "Unknown Project",
      };
    });

  return {
    tasks: filteredTasks,
    isLoading,
    error,
    projects,
    refetch, // Expose refetch function
  };
};
